/**
 * Coordinator-editable mail templates (SPEC §1.1E, §2 email).
 *
 * A template is subject + body per language. The body is HTML produced by the
 * Sporočila editor, and the only markup the renderer promises to understand is
 * what that editor can emit: paragraphs, h2/h3, ul/ol, hr, a, strong/em, plus
 * two node types of our own —
 *
 *   inline placeholder   <span data-ph="ime">{{ime}}</span>
 *   block placeholder    <div data-ph="razpored">{{razpored}}</div>
 *   call-to-action       <a data-cta href="{{povezava}}">Odpri brifing</a>
 *
 * Placeholders are also recognised as bare `{{ime}}` text, so a template typed
 * or pasted by hand still renders. Braces are the storage form; the editor's
 * chips are a view of them.
 *
 * Block placeholders carry app-rendered content (the shift list, the change
 * diff). When that content is empty the whole block is dropped rather than
 * leaving a stub sentence behind — a volunteer with no shifts yet gets a mail
 * that reads whole.
 *
 * A mail to a shared mailbox — a couple, a parent and their children — is one
 * mail rendered from the same template for several people at once: the
 * context carries `people`, the greeting reads their joined names, a block
 * lists each person's lines under their name, and the link button is repeated
 * once per person with their own link. See {@link MailContext.people}.
 */

import { LANGS, type Lang } from './constants';

// ---------------------------------------------------------------------------
// Catalogue
// ---------------------------------------------------------------------------

export const MAIL_TEMPLATE_KEYS = ['uvodno', 'napotnica', 'sprememba', 'zahvala', 'dnevno'] as const;
export type MailTemplateKey = (typeof MAIL_TEMPLATE_KEYS)[number];

/**
 * The templates a coordinator sends to chosen people — one at a time, from a
 * card, or in bulk from Prejemniki. The dnevno sporočilo is not one of them:
 * it is about one event day, and it goes out for that day alone, from its own
 * card on Predloge or from the 03:00 job (`modules/mail/dayMail.ts`).
 */
export const MANUAL_TEMPLATE_KEYS = ['uvodno', 'napotnica', 'sprememba', 'zahvala'] as const;
export type ManualTemplateKey = (typeof MANUAL_TEMPLATE_KEYS)[number];

export const isManualTemplateKey = (kind: string): kind is ManualTemplateKey =>
  (MANUAL_TEMPLATE_KEYS as readonly string[]).includes(kind);

/**
 * Mail that is nobody's word in a conversation: it asks nothing and answers
 * nothing, so "brez odgovora", "čaka naš odgovor" and the digest read past it.
 * An obvestilo is written for many at once, and a dnevno sporočilo is sent by
 * a job at 03:00 — made a last word, it would hide every question still
 * waiting for us on race morning.
 */
export const NOT_A_WORD_KINDS: readonly string[] = ['obvestilo', 'dnevno'];

/**
 * Every kind of mail the app renders: the four stored templates, and the
 * obvestilo — a one-off notice a coordinator writes for one send and never
 * stores as a template. Its words travel with its batch (`mail_notice`), and
 * everything downstream of them is the templates' own path.
 *
 * `MAIL_TEMPLATE_KEYS` is what is listed on Predloge and seeded into
 * `mail_template`; `MANUAL_TEMPLATE_KEYS` is what is counted on Prejemniki and
 * offered on the Pošiljanje card.
 */
export const MAIL_KINDS = [...MAIL_TEMPLATE_KEYS, 'obvestilo'] as const;
export type MailKind = (typeof MAIL_KINDS)[number];

export const isTemplateKey = (kind: string): kind is MailTemplateKey =>
  (MAIL_TEMPLATE_KEYS as readonly string[]).includes(kind);

export const PLACEHOLDER_KEYS = [
  'ime',
  'polno_ime',
  'dogodek',
  'datumi_dogodka',
  'telefon_operative',
  'poštni_predal',
  'koordinator',
  'koordinator_email',
  'povezava',
  'razpored',
  'spremembe',
] as const;
export type PlaceholderKey = (typeof PLACEHOLDER_KEYS)[number];

/** Which sidebar group a placeholder is offered under. */
export type PlaceholderGroup = 'oseba' | 'dogodek' | 'ekipa' | 'vsebina';

export interface PlaceholderDef {
  key: PlaceholderKey;
  group: PlaceholderGroup;
  /** True for app-rendered content that occupies a block of its own. */
  block: boolean;
  /** What it resolves to, in the coordinator's words. */
  hint: string;
  /** Stand-in shown when previewing without a volunteer. */
  sample: string;
}

export const PLACEHOLDERS: Record<PlaceholderKey, PlaceholderDef> = {
  ime: {
    key: 'ime',
    group: 'oseba',
    block: false,
    hint: 'Ime prostovoljca, brez priimka — za nagovor. Pri skupnem predalu imena vseh naslovnikov: »Ana in Miha«.',
    sample: 'Ana',
  },
  polno_ime: {
    key: 'polno_ime',
    group: 'oseba',
    block: false,
    hint: 'Ime in priimek, kot sta zapisana v evidenci. Pri skupnem predalu vsi naslovniki.',
    sample: 'Ana Novak',
  },
  dogodek: {
    key: 'dogodek',
    group: 'dogodek',
    block: false,
    hint: 'Ime dogodka z Nastavitev.',
    sample: 'Julian Alps Trail Run 2026',
  },
  datumi_dogodka: {
    key: 'datumi_dogodka',
    group: 'dogodek',
    block: false,
    hint: 'Datumi dogodka, izpisani na dolgo.',
    sample: '18.–20. september 2026',
  },
  telefon_operative: {
    key: 'telefon_operative',
    group: 'dogodek',
    block: false,
    hint: 'Telefon operativnega centra z Nastavitev.',
    sample: '+386 41 000 000',
  },
  'poštni_predal': {
    key: 'poštni_predal',
    group: 'dogodek',
    block: false,
    hint: 'Naslov, na katerega prostovoljci odgovarjajo.',
    sample: 'reditelji@julianalpstrail.run',
  },
  koordinator: {
    key: 'koordinator',
    group: 'ekipa',
    block: false,
    hint: 'Ime koordinatorja, ki pošlje to sporočilo.',
    sample: 'Jernej Cvek',
  },
  koordinator_email: {
    key: 'koordinator_email',
    group: 'ekipa',
    block: false,
    hint: 'E-naslov koordinatorja, ki pošlje to sporočilo.',
    sample: 'koordinator@julianalpstrail.run',
  },
  povezava: {
    key: 'povezava',
    group: 'vsebina',
    block: false,
    hint: 'Osebna povezava do brifinga. Pošiljanje jo vsakič zavrti na novo — razen dnevnega sporočila, ki doda dodatno povezavo, veljavno do tedna po dogodku. Pri skupnem predalu dobi vsak naslovnik svoj gumb.',
    sample: 'https://reditelji.julianalpstrail.run/b/…',
  },
  razpored: {
    key: 'razpored',
    group: 'vsebina',
    block: true,
    hint: 'Seznam izmen prostovoljca. Če razporeda še ni, odstavek izpade. V uvodnem pismu pove le dan, uro in kraj — brez oznake mesta. V dnevnem sporočilu samo izmene tega dne, ki jih je prostovoljec potrdil, z zbornim mestom. Pri skupnem predalu po osebah.',
    sample: '',
  },
  spremembe: {
    key: 'spremembe',
    group: 'vsebina',
    block: true,
    hint: 'Kaj se je spremenilo od zadnje poslane napotnice. Brez sprememb odstavek izpade. Pri skupnem predalu po osebah.',
    sample: '',
  },
};

export const PLACEHOLDER_GROUP_LABEL: Record<PlaceholderGroup, string> = {
  oseba: 'Prostovoljec',
  dogodek: 'Dogodek',
  ekipa: 'Pošiljatelj',
  vsebina: 'Vsebina',
};

export interface MailTemplateDef {
  key: MailKind;
  label: string;
  /** One line on what this mail is for, shown under its name. */
  lede: string;
  /** Placeholders offered — and accepted — in this template. */
  allows: readonly PlaceholderKey[];
  /** Placeholders whose absence is worth a warning at save time. */
  expects: readonly PlaceholderKey[];
  /** Whether the editor offers a call-to-action button. */
  buttons: boolean;
  /**
   * How much of a shift ⟦razpored⟧ spells out. Omitted means the whole post —
   * its oznaka and micro-location — which is what a volunteer who has already
   * been told where to stand expects to read back.
   */
  scheduleDetail?: ScheduleDetail;
}

/**
 * `'sector'` names only the macro location a shift falls in — Bled, Kranjska
 * Gora — and leaves the oznaka and the micro-location out. `'meet'` is the
 * whole post plus the meeting time and place: what somebody walking out of the
 * door that morning needs.
 */
export type ScheduleDetail = 'sector' | 'post' | 'meet';

const PERSON_AND_EVENT = [
  'ime',
  'polno_ime',
  'dogodek',
  'datumi_dogodka',
  'telefon_operative',
  'poštni_predal',
  'koordinator',
  'koordinator_email',
] as const;

export const MAIL_TEMPLATES: Record<MailTemplateKey, MailTemplateDef> = {
  uvodno: {
    key: 'uvodno',
    label: 'Uvodno pismo',
    lede: 'Prvi stik: skupne informacije in vabilo, naj se prostovoljec javi z odgovorom.',
    // Deliberately no {{povezava}}: the introduction carries no magic link, so
    // sending it never rotates a token (see modules/mail/send.ts).
    allows: [...PERSON_AND_EVENT, 'razpored'],
    expects: [],
    buttons: true,
    /*
     * Day, time and the macro location, and no more. What this mail asks for is
     * a yes — whether the person can give us that weekend at all — and the
     * oznaka and the micro-location are not part of that question. They are
     * also the parts still being moved around while the roster settles, so
     * naming them here would promise a post before anyone has committed to it.
     * The napotnica is where a volunteer is told exactly where to stand.
     */
    scheduleDetail: 'sector',
  },
  napotnica: {
    key: 'napotnica',
    label: 'Napotnica',
    lede: 'Osebni razpored in povezava do brifinga, s katero prostovoljec potrdi udeležbo.',
    allows: [...PERSON_AND_EVENT, 'povezava', 'razpored'],
    expects: ['povezava'],
    buttons: true,
  },
  sprememba: {
    key: 'sprememba',
    label: 'Obvestilo o spremembi',
    lede: 'Kaj se je od zadnje napotnice spremenilo in prošnja za ponovno potrditev.',
    allows: [...PERSON_AND_EVENT, 'povezava', 'razpored', 'spremembe'],
    expects: ['spremembe'],
    buttons: true,
  },
  zahvala: {
    key: 'zahvala',
    label: 'Zahvala',
    lede: 'Po dogodku, vsem, ki so odstali svojo izmeno.',
    allows: PERSON_AND_EVENT,
    expects: [],
    buttons: true,
  },
  dnevno: {
    key: 'dnevno',
    label: 'Dnevno sporočilo',
    lede: 'Na dan dogodka ob 03:00, tistim, ki so ta dan izmeno potrdili: današnje izmene in povezava za prijavo prihoda.',
    allows: [...PERSON_AND_EVENT, 'povezava', 'razpored'],
    expects: ['razpored', 'povezava'],
    buttons: true,
    scheduleDetail: 'meet',
  },
};

/**
 * What every mail kind may carry — the templates, and the obvestilo.
 *
 * The obvestilo knows the person, the event and the sender, and nothing that
 * reaches into the lifecycle: no ⟦povezava⟧, so a send never rotates a link;
 * no ⟦razpored⟧ or ⟦spremembe⟧, so it is never read as a napotnica and never
 * moves the baseline "Kaj se je spremenilo" diffs against. No button either —
 * it is written once, for one send, and a button is a layout that has to be
 * right in both languages.
 */
export const MAIL_DEFS: Record<MailKind, MailTemplateDef> = {
  ...MAIL_TEMPLATES,
  obvestilo: {
    key: 'obvestilo',
    label: 'Obvestilo',
    lede: 'Enkratno sporočilo izbranim prostovoljcem — ne shrani se med predloge.',
    allows: PERSON_AND_EVENT,
    expects: [],
    buttons: false,
  },
};

// ---------------------------------------------------------------------------
// Bulk sends
// ---------------------------------------------------------------------------

/**
 * A bulk send is a record, not a response.
 *
 * `pending` until a worker picks it up, `running` while it drains, `done` when
 * every recipient has an outcome. There is deliberately no `failed`: a batch
 * that could not reach somebody records that on the person, not on the batch,
 * so a partial send still says exactly who got what.
 */
export const MAIL_BATCH_STATUSES = ['pending', 'running', 'done'] as const;
export type MailBatchStatus = (typeof MAIL_BATCH_STATUSES)[number];

/**
 * `waiting` until attempted; then `sent`, `skipped` (a rule refused it — no
 * e-mail address, no shifts) or `failed` (the provider did). Only `waiting`
 * items are ever picked up, which is what makes a retry resume a batch rather
 * than mail it twice.
 */
export const MAIL_BATCH_ITEM_STATUSES = ['waiting', 'sent', 'skipped', 'failed'] as const;
export type MailBatchItemStatus = (typeof MAIL_BATCH_ITEM_STATUSES)[number];

export interface MailBatchCounts {
  total: number;
  sent: number;
  skipped: number;
  failed: number;
  waiting: number;
}

export function countBatch(items: { status: MailBatchItemStatus }[]): MailBatchCounts {
  const counts = { total: items.length, sent: 0, skipped: 0, failed: 0, waiting: 0 };
  for (const i of items) counts[i.status] += 1;
  return counts;
}

// ---------------------------------------------------------------------------
// Who can receive what
// ---------------------------------------------------------------------------

/**
 * The shift states that count as "this person has a schedule".
 *
 * A proposed fill is a coordinator's draft, not something to mail about, so it
 * is excluded here exactly as it is in `briefingShifts` on the server.
 */
export const MAIL_SHIFT_STATES = ['assigned', 'done'] as const;

export interface MailEligibility {
  /**
   * A place in an external team rather than a person we brief (SPEC §1.1J).
   * Nobody writes to a group's slot, so this refuses every template — and
   * because a magic link is only ever minted inside a send, refusing here is
   * also what keeps a slot from acquiring a briefing token.
   */
  isExternal: boolean;
  /**
   * Withdrew from the event — "Ne pride" (SPEC §1.1K). Nothing about a schedule
   * goes to them any more; only the thank-you, and only when they kept a shift,
   * which after a withdrawal is exactly a shift they worked or were working.
   */
  isCancelled: boolean;
  hasEmail: boolean;
  /** Shifts in MAIL_SHIFT_STATES. */
  shiftCount: number;
  /** A napotnica has gone out, so "Kaj se je spremenilo" has a baseline. */
  hasBriefing: boolean;
  /**
   * Something has actually changed since that briefing.
   *
   * The server knows this exactly (it holds the diff); the UI approximates it
   * from the schedule version, which moves only on a material change. The
   * approximation is the stricter of the two, so the UI never offers a send the
   * server would refuse.
   */
  hasChanges: boolean;
}

/**
 * Why a template cannot go to this volunteer right now, or null.
 *
 * Shared because both sides need the same answer for different reasons: the
 * server refuses the send, and the UI greys the button out before one is
 * attempted. Two copies drifted once already — the web counted proposed shifts
 * the server would not — which is what this rule being in one place prevents.
 */
export function blockedReason(
  key: MailKind,
  e: MailEligibility,
  send: { includeCancelled?: boolean } = {},
): string | null {
  // First, and before any reason that could be fixed: a slot's group is briefed
  // by its own leader, so there is nothing here to chase up.
  if (e.isExternal) return 'zunanja ekipa';
  // Also ahead of the fixable reasons: there is no schedule left to tell them
  // about. The thank-you stays for somebody who kept a shift — after a
  // withdrawal those are shifts already worked or under way — and an obvestilo
  // reaches them when the coordinator ticked them in on purpose.
  if (
    e.isCancelled &&
    !(key === 'zahvala' && e.shiftCount > 0) &&
    !(key === 'obvestilo' && send.includeCancelled === true)
  ) {
    return 'ne pride';
  }
  if (!e.hasEmail) return 'brez e-naslova';
  if (key === 'napotnica' && e.shiftCount === 0) return 'brez izmen';
  // Its context holds only that day's agreed shifts (`dayMailShifts`).
  if (key === 'dnevno' && e.shiftCount === 0) return 'ta dan brez potrjene izmene';
  if (key === 'sprememba') {
    if (!e.hasBriefing) return 'ni izhodišča — najprej pošlji napotnico';
    // A change notice with nothing in it asks for a re-confirmation the
    // volunteer does not owe, and its send would reset their diff baseline —
    // swallowing whatever really is pending.
    if (!e.hasChanges) return 'od zadnje napotnice se ni nič spremenilo';
  }
  return null;
}

/**
 * Whether this mail carries the volunteer's personal briefing link.
 *
 * The one place that decides it, because the answer has to be the same for the
 * preview a coordinator approves and the mail that then goes out: a template
 * that may not mint a link must not be shown one either.
 */
export function wantsLink(key: MailKind, subject: string, body: string): boolean {
  return (
    MAIL_DEFS[key].allows.includes('povezava') &&
    placeholdersIn(`${subject} ${body}`).includes('povezava')
  );
}

// ---------------------------------------------------------------------------
// Markup helpers
// ---------------------------------------------------------------------------

/** Placeholder names carry Slovenian letters, so \w is not enough. */
const NAME = '[A-Za-zČŠŽĆĐčšžćđ0-9_]+';
const BARE_RE = new RegExp(`\\{\\{\\s*(${NAME})\\s*\\}\\}`, 'g');
const INLINE_NODE_RE = new RegExp(`<span[^>]*data-ph="(${NAME})"[^>]*>.*?</span>`, 'g');
/*
 * `[^<]*` rather than `.*?`: a block placeholder's content is its own chip text
 * and nothing else. A non-greedy match across markup stops at the first closing
 * tag, so a body that reached storage with a nested element inside the block —
 * a hand edit, a paste, a translation that wrapped the chip — had the block
 * replaced and its outer `</div>` left stranded in the mail.
 */
const BLOCK_NODE_RE = new RegExp(`<div[^>]*data-ph="(${NAME})"[^>]*>[^<]*</div>`, 'g');
const CTA_RE = /<a([^>]*\bdata-cta\b[^>]*)>(.*?)<\/a>/g;

export const isPlaceholderKey = (s: string): s is PlaceholderKey =>
  (PLACEHOLDER_KEYS as readonly string[]).includes(s);

export function escapeMailHtml(s: string): string {
  return s
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

/** Every placeholder name a body refers to, in either notation, deduplicated. */
export function placeholdersIn(html: string): string[] {
  const found = new Set<string>();
  for (const m of html.matchAll(INLINE_NODE_RE)) found.add(m[1]!);
  for (const m of html.matchAll(BLOCK_NODE_RE)) found.add(m[1]!);
  for (const m of html.matchAll(BARE_RE)) found.add(m[1]!);
  return [...found];
}

export interface TemplateCheck {
  /** Names the template uses that no placeholder answers to (typos). */
  unknown: string[];
  /** Known placeholders this template is not allowed to use. */
  notAllowed: PlaceholderKey[];
  /** Placeholders the template is expected to carry but doesn't. */
  missing: PlaceholderKey[];
  /**
   * Block placeholders used in the subject line.
   *
   * A subject is one line; a shift list in it is never what anyone meant. It
   * still renders — flattened onto that line — rather than shipping braces, but
   * it is worth saying.
   */
  blockInSubject: PlaceholderKey[];
}

/**
 * What is wrong with a body before it reaches 150 inboxes. Nothing here blocks
 * a save — a coordinator mid-edit is allowed an unfinished template — but the
 * editor and the send dialog both surface it.
 */
export function checkTemplate(key: MailKind, subject: string, body: string): TemplateCheck {
  const def = MAIL_DEFS[key];
  const used = placeholdersIn(`${subject} ${body}`);
  const unknown = used.filter((n) => !isPlaceholderKey(n));
  const notAllowed = used.filter(
    (n): n is PlaceholderKey => isPlaceholderKey(n) && !def.allows.includes(n),
  );
  const missing = def.expects.filter((n) => !used.includes(n));
  const blockInSubject = placeholdersIn(subject).filter(
    (n): n is PlaceholderKey => isPlaceholderKey(n) && PLACEHOLDERS[n].block,
  );
  return { unknown, notAllowed, missing: [...missing], blockInSubject };
}

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

/** One line of an app-rendered block: a bold lead, then the rest. */
export interface MailBlockLine {
  lead: string;
  rest: string;
  /**
   * Whose line this is, in a mail several people read. Lines carrying a name
   * render grouped under it; a block whose lines carry none renders as one
   * list, exactly as a mail to one person.
   */
  person?: string;
}

/** One of the people a mail to a shared mailbox is addressed to. */
export interface MailPerson {
  /** First name — what a button label or a heading calls them. */
  ime: string;
  polno_ime: string;
  /** Their own briefing link; absent when this mail carries none. */
  povezava?: string;
}

export interface MailContext {
  inline: Record<string, string>;
  blocks: Record<string, MailBlockLine[]>;
  /**
   * Everyone this one mail is addressed to, when they share an inbox (skupni
   * predal). From two people up the renderer turns a link button into one per
   * person, resolves a bare ⟦povezava⟧ to every link named, and reads
   * `inline.ime` as the joined names the server bound there. Absent, or one
   * person, renders exactly as it always has: the single person's own values
   * are what `inline` holds.
   */
  people?: MailPerson[];
}

/*
 * Placeholder names come from a coordinator's keyboard, so every lookup into a
 * context goes through `Object.hasOwn`. A plain `ctx.inline[name]` would find
 * `toString` or `constructor` on the prototype chain, pass an `!== undefined`
 * guard, and either render "function toString() { [native code] }" into an
 * inbox or throw — a typo would take down the preview and every send of the
 * template it was saved into.
 */
const inlineValue = (ctx: MailContext, name: string): string | undefined =>
  Object.hasOwn(ctx.inline, name) ? ctx.inline[name] : undefined;

const blockValue = (ctx: MailContext, name: string): MailBlockLine[] | undefined =>
  Object.hasOwn(ctx.blocks, name) ? ctx.blocks[name] : undefined;

/** From two people up this mail reads differently: see {@link MailContext.people}. */
const isShared = (ctx: MailContext): boolean => (ctx.people?.length ?? 0) >= 2;

/**
 * The context as one of the people reads it: their own name and link in place
 * of the household's, and no `people`, so everything below renders the
 * single-person way. This is how a button is cloned per person.
 */
function personView(ctx: MailContext, p: MailPerson): MailContext {
  const inline: Record<string, string> = { ...ctx.inline, ime: p.ime, polno_ime: p.polno_ime };
  if (p.povezava === undefined) delete inline.povezava;
  else inline.povezava = p.povezava;
  return { inline, blocks: ctx.blocks };
}

/**
 * One person in `people` is not a household — it is the ordinary case handed
 * over in a slightly different shape — so it is folded into `inline` up front
 * and every pass below sees exactly what a single-person context always was.
 */
const normalizeCtx = (ctx: MailContext): MailContext =>
  ctx.people?.length === 1 ? personView(ctx, ctx.people[0]!) : ctx;

/**
 * An inline value, with the one household twist: ⟦povezava⟧ in running text —
 * a sentence, the subject — becomes every person's link, named, since one URL
 * cannot stand for two people. Unresolved while anyone lacks a link, and never
 * read from `inline`, where a single link would be a bug.
 */
function lookupInline(ctx: MailContext, name: string): string | undefined {
  if (name === 'povezava' && isShared(ctx)) {
    const people = ctx.people!;
    if (people.some((p) => p.povezava === undefined)) return undefined;
    return people.map((p) => `${p.ime}: ${p.povezava}`).join(', ');
  }
  return inlineValue(ctx, name);
}

const POVEZAVA_RE = /\{\{\s*povezava\s*\}\}/;
const HREF_RE = /href="([^"]*)"/;
/** Anchors that are not the call-to-action — a link the editor set on a word. */
const PLAIN_LINK_RE = /<a(?=[\s>])((?:(?!\bdata-cta\b)[^>])*)>(.*?)<\/a>/g;

const hrefOf = (attrs: string): string => decodeEntities(HREF_RE.exec(attrs)?.[1] ?? '');

/**
 * A cloned link's text: the coordinator's label with ⟦ime⟧ resolved to this
 * person where it uses one, otherwise the label with the name appended —
 * "Odpri brifing — Ana" — so the reader knows whose button this is.
 */
function personLabel(label: string, view: MailContext, p: MailPerson): string {
  const named = placeholdersIn(label).some((n) => n === 'ime' || n === 'polno_ime');
  if (!named) return `${label} — ${escapeMailHtml(p.ime)}`;
  // Anything the label leaves unresolved is reported by the inline pass that
  // runs over the whole body afterwards, so nothing is lost by dropping these.
  const scratch = new Set<string>();
  return label
    .replaceAll(INLINE_NODE_RE, (whole, name: string) => substitute(whole, name, view, scratch))
    .replaceAll(BARE_RE, (whole, name: string) => substitute(whole, name, view, scratch));
}

export interface RenderedMail {
  subject: string;
  html: string;
  text: string;
  /** Placeholder names left standing because nothing resolved them. */
  unresolved: string[];
}

const LINK = '#194b5b';
const RULE = '#dde3e6';

/** Mail clients ignore stylesheets, so every block carries its own spacing. */
const BLOCK_STYLE: Record<string, string> = {
  p: 'margin:0 0 14px;',
  h2: 'margin:22px 0 8px;font-size:18px;line-height:1.3;font-weight:700;',
  h3: 'margin:18px 0 6px;font-size:15px;line-height:1.35;font-weight:700;',
  ul: 'margin:0 0 14px;padding-left:20px;',
  ol: 'margin:0 0 14px;padding-left:20px;',
  li: 'margin:0 0 4px;',
};

/**
 * Give every block its own inline spacing — but only where there is none.
 * `blockHtml` already styles the list it builds, and a second pass over it
 * would emit two `style` attributes on one tag: browsers keep the first, strict
 * mail sanitizers may drop the element.
 */
function inlineStyles(html: string): string {
  let out = html;
  for (const [tag, style] of Object.entries(BLOCK_STYLE)) {
    out = out.replaceAll(
      new RegExp(`<${tag}(?![^>]*\\bstyle=)(?=[\\s>])`, 'g'),
      `<${tag} style="${style}"`,
    );
  }
  out = out.replaceAll(
    /<hr\s*\/?>/g,
    `<hr style="border:0;border-top:1px solid ${RULE};margin:20px 0;">`,
  );
  // Plain links only — the CTA is already a table by the time we get here.
  out = out.replaceAll(/<a (?![^>]*\bstyle=)/g, `<a style="color:${LINK};" `);
  return out;
}

/** A button mail clients render: a table cell, not a styled anchor. */
function ctaHtml(href: string, label: string): string {
  return [
    `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:4px 0 18px;">`,
    `<tr><td style="border-radius:8px;background:${LINK};">`,
    `<a href="${escapeMailHtml(href)}" style="display:inline-block;padding:11px 22px;color:#ffffff;`,
    `font-weight:700;font-size:15px;text-decoration:none;border-radius:8px;">${label}</a>`,
    `</td></tr></table>`,
  ].join('');
}

/** A line flattened to text: "Sobota · 08:00–14:00", or "Miha: Sobota · …". */
function lineText(l: MailBlockLine): string {
  const body = [l.lead, l.rest].filter(Boolean).join(' · ');
  return l.person ? `${l.person}: ${body}` : body;
}

function listHtml(lines: MailBlockLine[]): string {
  const items = lines
    .map((l) => {
      const rest = l.rest ? escapeMailHtml(l.rest) : '';
      // A stub line — "še brez izmen" under somebody's name — carries no lead.
      const text = l.lead ? `<b>${escapeMailHtml(l.lead)}</b>${rest ? ` · ${rest}` : ''}` : rest;
      return `<li style="${BLOCK_STYLE.li}">${text}</li>`;
    })
    .join('');
  return `<ul style="${BLOCK_STYLE.ul}">${items}</ul>`;
}

/**
 * A block's lines as a list — or, when they belong to several people, as one
 * list per person under their name, in the order the lines first name them.
 */
function blockHtml(lines: MailBlockLine[]): string {
  if (!lines.some((l) => l.person)) return listHtml(lines);
  const people = [...new Set(lines.map((l) => l.person ?? ''))];
  return people
    .map((person) => {
      const own = listHtml(lines.filter((l) => (l.person ?? '') === person));
      if (!person) return own;
      return `<p style="margin:0 0 4px;font-weight:700;">${escapeMailHtml(person)}</p>${own}`;
    })
    .join('');
}

/**
 * Marks where an empty block stood, until its lead-in has been considered.
 *
 * A private-use code point: nothing a keyboard produces collides with it, and
 * unlike a NUL it neither makes this file read as binary to grep nor risks a
 * value Postgres refuses to store if one ever escaped the pass below.
 */
const DROPPED = '\u{E000}';

/**
 * Fences a placeholder the CTA pass has already decided not to resolve, so the
 * inline pass that follows cannot resolve it after all.
 *
 * Without it a link target keeps its braces through step 2 and then picks up
 * the block fallback in step 3 — and a shift list lands in an href.
 */
const KEPT_OPEN = '\u{E001}';
const KEPT_CLOSE = '\u{E002}';

const fence = (s: string) => s.replaceAll('{{', KEPT_OPEN).replaceAll('}}', KEPT_CLOSE);
const unfence = (s: string) => s.replaceAll(KEPT_OPEN, '{{').replaceAll(KEPT_CLOSE, '}}');

/**
 * A paragraph that ends in a colon introduces what follows it, so when the
 * block it introduces is dropped, the introduction goes too.
 *
 * Without this the uvodno pismo — the one mail whose whole purpose is to reach
 * someone with no schedule yet — ends on "Zate trenutno predvidevamo:" and then
 * nothing. The colon is the signal a writer already gives; this just honours it.
 */
function dropLeadIns(html: string): string {
  return html
    .replaceAll(
      new RegExp(`<p\\b[^>]*>(?:(?!</p>)[\\s\\S])*:\\s*</p>\\s*${DROPPED}`, 'g'),
      '',
    )
    .replaceAll(DROPPED, '');
}

/**
 * Resolve a body against a context. Unknown names are left standing verbatim
 * and reported — a typo shows up as `{{razpred}}` in the preview instead of
 * quietly vanishing from the mail.
 */
export function renderBody(body: string, input: MailContext): { html: string; unresolved: string[] } {
  const ctx = normalizeCtx(input);
  const unresolved = new Set<string>();

  // 1. Blocks first: an empty one takes its element with it.
  let out = body.replaceAll(BLOCK_NODE_RE, (whole, name: string) => {
    const lines = blockValue(ctx, name);
    if (lines === undefined) {
      unresolved.add(name);
      return whole;
    }
    return lines.length === 0 ? DROPPED : blockHtml(lines);
  });

  // A block placeholder typed as bare text sits in a paragraph of its own.
  // Drop that paragraph whole when the block is empty, by the same rule.
  for (const [name, lines] of Object.entries(ctx.blocks)) {
    const alone = new RegExp(
      `<(p|div)[^>]*>\\s*(?:<span[^>]*data-ph="${name}"[^>]*>.*?</span>|\\{\\{\\s*${name}\\s*\\}\\})\\s*</\\1>`,
      'g',
    );
    // A replacement *function*: volunteer data reaches this string, and `$&`
    // or `$'` in a place name would otherwise be expanded as a replacement
    // pattern instead of printed.
    const replacement = lines.length === 0 ? DROPPED : blockHtml(lines);
    out = out.replaceAll(alone, () => replacement);
  }

  out = dropLeadIns(out);

  // 2. Several people, several links: a link a coordinator set on a word whose
  //    target is ⟦povezava⟧ is cloned once per person, each with their own name
  //    on it. Only for a shared mailbox — for one person the inline pass below
  //    resolves the href as it always has.
  if (isShared(ctx)) {
    out = out.replaceAll(PLAIN_LINK_RE, (whole, attrs: string, label: string) => {
      const href = hrefOf(attrs);
      if (!POVEZAVA_RE.test(href)) return whole;
      return ctx
        .people!.map((p) => {
          const view = personView(ctx, p);
          const target = resolveInline(href, view, unresolved, { escape: false, blocks: false });
          const own = attrs.replace(HREF_RE, () => `href="${escapeMailHtml(fence(target))}"`);
          return `<a${own}>${personLabel(label, view, p)}</a>`;
        })
        .join(', ');
    });
  }

  // 3. CTA buttons, before generic link styling touches them. One per person
  //    when the mailbox is shared and the button leads to ⟦povezava⟧; a button
  //    with a fixed address is one button however many read the mail.
  out = out.replaceAll(CTA_RE, (_whole, attrs: string, label: string) => {
    const href = hrefOf(attrs);
    const views =
      isShared(ctx) && POVEZAVA_RE.test(href)
        ? ctx.people!.map((p) => ({ view: personView(ctx, p), person: p }))
        : [{ view: ctx, person: null }];
    return views
      .map(({ view, person }) => {
        const target = resolveInline(href, view, unresolved, { escape: false, blocks: false });
        return ctaHtml(fence(target), person ? personLabel(label, view, person) : label);
      })
      .join('');
  });

  // 4. Inline placeholders, in both notations.
  out = out.replaceAll(INLINE_NODE_RE, (whole, name: string) =>
    substitute(whole, name, ctx, unresolved),
  );
  out = out.replaceAll(BARE_RE, (whole, name: string) => substitute(whole, name, ctx, unresolved));

  return { html: unfence(inlineStyles(out)), unresolved: [...unresolved] };
}

function substitute(
  whole: string,
  name: string,
  ctx: MailContext,
  unresolved: Set<string>,
): string {
  const value = lookupInline(ctx, name);
  if (value !== undefined) return escapeMailHtml(value);
  // A block placeholder that survived step 1 sits inline in a sentence. Render
  // it on one line rather than dropping the coordinator's own words with it.
  const lines = blockValue(ctx, name);
  if (lines !== undefined) {
    return lines.map((l) => escapeMailHtml(lineText(l))).join('; ');
  }
  unresolved.add(name);
  return whole;
}

/**
 * Placeholder substitution for plain-text positions: the subject line, and a
 * link's href.
 *
 * `blocks` says whether app-composed content may resolve here. The subject
 * allows it — flattened onto one line, because braces in an inbox are worse
 * than a long subject — while an href does not, since a shift list is not a
 * URL and leaving it unresolved is what surfaces the mistake.
 */
function resolveInline(
  raw: string,
  ctx: MailContext,
  unresolved: Set<string>,
  opts: { escape: boolean; blocks: boolean },
): string {
  return raw.replaceAll(BARE_RE, (whole, name: string) => {
    const value = lookupInline(ctx, name);
    if (value !== undefined) return opts.escape ? escapeMailHtml(value) : value;

    const lines = opts.blocks ? blockValue(ctx, name) : undefined;
    if (lines !== undefined) return lines.map(lineText).join('; ');
    unresolved.add(name);
    return whole;
  });
}

export function renderSubject(
  subject: string,
  ctx: MailContext,
): { subject: string; unresolved: string[] } {
  const unresolved = new Set<string>();
  // Unwrap the editor's chip markup, but nothing more: the subject is authored
  // as plain text, so stripping tags here would silently eat a coordinator's
  // own angle brackets ("Napotnica <nujno>").
  const bare = subject.replaceAll(INLINE_NODE_RE, (_w, name: string) => `{{${name}}}`);
  return {
    subject: resolveInline(bare, normalizeCtx(ctx), unresolved, { escape: false, blocks: true }),
    unresolved: [...unresolved],
  };
}

const ENTITIES: Record<string, string> = {
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
  '&nbsp;': ' ',
  '&rsquo;': '’',
  '&lsquo;': '‘',
  '&rdquo;': '”',
  '&ldquo;': '“',
  '&mdash;': '—',
  '&ndash;': '–',
  '&hellip;': '…',
  '&amp;': '&',
};

const decodeEntities = (s: string) => {
  // Numeric first, and `&amp;` last of all: decoding `&amp;` early turns a
  // literal `&#8212;` somebody typed into an em dash, and then the text part
  // and the HTML part of one mail no longer say the same thing.
  let out = s.replaceAll(/&#(\d{1,7});/g, (whole, dec: string) => codePoint(Number(dec), whole));
  out = out.replaceAll(/&#[xX]([0-9a-fA-F]{1,6});/g, (whole, hex: string) =>
    codePoint(Number.parseInt(hex, 16), whole),
  );
  for (const [entity, char] of Object.entries(ENTITIES)) {
    if (entity === '&amp;') continue;
    out = out.replaceAll(entity, char);
  }
  return out.replaceAll('&amp;', '&');
};

/** The character, or the entity untouched when it names no usable one. */
const codePoint = (code: number, whole: string) => {
  if (!Number.isFinite(code) || code <= 0 || code > 0x10ffff) return whole;
  // A surrogate is half a character. Decoded on its own it survives every
  // string operation and is then rewritten on the way into the database.
  if (code >= 0xd800 && code <= 0xdfff) return whole;
  try {
    return String.fromCodePoint(code);
  } catch {
    return whole;
  }
};

/**
 * The blank lines between a sender's paragraphs brought down to one, and each
 * line's trailing whitespace — a CRLF's carriage return with it — gone.
 *
 * For reading a mail, not for storing it. A client that writes its
 * `text/plain` part out of the HTML the sender typed counts a paragraph's
 * spacing twice — Zoho's leaves three to seven blank lines between every line
 * of a reply — and `white-space: pre-wrap` would show a coordinator every one
 * of them. The row keeps what the server sent; the thread and the AI prompt
 * read it through this, and `htmlToText` ends on it.
 */
export function tidyBlankLines(text: string): string {
  return text
    .split('\n')
    .map((line) => line.trimEnd())
    .join('\n')
    .replaceAll(/\n{3,}/g, '\n\n');
}

/**
 * The plain text inside some HTML. Links keep their address — a text-only
 * client still gets the volunteer to their briefing.
 *
 * Used both ways: for the app's own rendered mail, where the two parts must
 * carry the same words, and for an HTML mail read off the server, which is
 * written by somebody else's client and can contain anything. That second use
 * is why the invisible elements go first — `stripTags` takes the tags and
 * leaves what is between them, so a `<style>` block would otherwise arrive in
 * the thread as a wall of CSS.
 */
export function htmlToText(html: string): string {
  let out = html;
  // Unclosed as well as closed: a `<style>` whose tail was cut off would
  // otherwise reach the reader as CSS, and a truncated mail is the ordinary
  // way that happens.
  out = out.replaceAll(/<(style|script)\b[^>]*>[\s\S]*?(?:<\/\1\s*>|$)/gi, '');
  // `<head>` is not in that list, because its end tag is optional and senders
  // routinely omit it: falling back to end-of-string would swallow the whole
  // message and file it as an empty mail. It ends where it says it ends, or
  // where the body starts, and otherwise the tag alone is dropped by stripTags.
  out = out.replaceAll(/<head\b[^>]*>[\s\S]*?(?:<\/head\s*>|(?=<body\b))/gi, '');
  // A comment can hold a `>` — Outlook's conditionals do — so stripTags alone
  // would cut it in the wrong place and leave the tail as text.
  out = out.replaceAll(/<!--[\s\S]*?(?:-->|$)/g, '');
  out = out.replaceAll(
    // The href may be double-quoted, single-quoted or bare, and the anchor may
    // be wrapped across lines: a mail client writes all four, and a missed one
    // costs the reader the address.
    /<a\b[^>]*\bhref\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>]+))[^>]*>([\s\S]*?)<\/a\s*>/gi,
    (_w, dq: string, sq: string, bare: string, label: string) => {
      const href = (dq ?? sq ?? bare ?? '').trim();
      const text = stripTags(label).trim();
      if (!href) return text;
      return text && text !== href ? `${text}: ${href}` : href;
    },
  );
  out = out.replaceAll(/<br\b[^>]*\/?>/gi, '\n');
  out = out.replaceAll(/<li\b[^>]*>/gi, '\n- ');
  out = out.replaceAll(/<\/(p|h1|h2|h3|h4|ul|ol|table|tr|div|blockquote)\s*>/gi, '\n\n');
  out = out.replaceAll(/<hr\b[^>]*>/gi, '\n---\n');
  out = decodeEntities(stripTags(out));
  return tidyBlankLines(
    out
      .split('\n')
      .map((line) => line.replace(/[ \t]+/g, ' '))
      .join('\n'),
  ).trim();
}

/**
 * Tags out, text kept. A quoted attribute may hold a `>` — `alt="x > y"` is
 * ordinary — so quoted runs are consumed before the closing bracket is looked
 * for, which a bare `<[^>]*>` cuts short.
 */
const stripTags = (s: string) => s.replaceAll(/<\/?[a-zA-Z][^\s>]*(?:"[^"]*"|'[^']*'|[^"'>])*>/g, '');

/** Subject + body + derived text, ready for the mailer. */
export function renderMail(
  template: { subject: string; body: string },
  ctx: MailContext,
): RenderedMail {
  const subject = renderSubject(template.subject, ctx);
  const body = renderBody(template.body, ctx);
  return {
    subject: subject.subject,
    html: body.html,
    text: htmlToText(body.html),
    unresolved: [...new Set([...subject.unresolved, ...body.unresolved])],
  };
}

// ---------------------------------------------------------------------------
// Obvestilo — whether a one-off notice may go out
// ---------------------------------------------------------------------------

/**
 * A call-to-action anywhere in a body: an `<a>` carrying the `data-cta`
 * attribute. Quoted attribute values are stepped over whole, so a link whose
 * address or title merely contains the words is not one, and the name has to
 * end where an attribute name ends. Its own non-global expression: `CTA_RE` is
 * global, and `.test()` on a global one resumes from where the last call
 * stopped, so asking twice would not give the same answer.
 */
const CTA_ATTR_RE = /<a\b(?:"[^"]*"|'[^']*'|[^>"'])*?\sdata-cta(?=[\s=/>])/i;
export const hasCta = (html: string): boolean => CTA_ATTR_RE.test(html);

/** Nothing to send in this language: no subject, or a body without a word. */
export function isBlankMail(t: MailTemplateBody): boolean {
  return t.subject.trim() === '' || htmlToText(t.body) === '';
}

const LANG_NAME: Record<Lang, string> = { sl: 'slovensko', en: 'angleško' };

/**
 * Why this obvestilo cannot go out, in the coordinator's words — empty when it
 * can. The one spelling of the question: the send button asks it of the draft
 * and the server asks it again of the batch before a row is written.
 *
 * `langs` is who will actually be written to. A language nobody on the list
 * reads may be left empty; one somebody reads may not, or they would get a
 * mail with no subject. Placeholders are checked in every language that has
 * words, since a typo there would reach an inbox as literal braces.
 */
export function noticeProblems(
  bodies: Record<Lang, MailTemplateBody>,
  langs: readonly Lang[],
): string[] {
  const problems: string[] = [];
  for (const lang of LANGS) {
    if (!langs.includes(lang) || !isBlankMail(bodies[lang])) continue;
    const name = LANG_NAME[lang];
    problems.push(`Manjka ${name} besedilo — med prejemniki so tudi prostovoljci, ki berejo ${name}.`);
  }

  const unknown = new Set<string>();
  const notAllowed = new Set<string>();
  let button = false;
  for (const lang of LANGS) {
    const { subject, body } = bodies[lang];
    if (subject.trim() === '' && body.trim() === '') continue;
    const check = checkTemplate('obvestilo', subject, body);
    for (const n of check.unknown) unknown.add(n);
    for (const n of check.notAllowed) notAllowed.add(n);
    button ||= hasCta(body);
  }
  if (unknown.size > 0) problems.push(`Ni takšne oznake: ${[...unknown].join(', ')}.`);
  if (notAllowed.size > 0) problems.push(`Obvestilo ne pozna oznak: ${[...notAllowed].join(', ')}.`);
  if (button) problems.push('Obvestilo ne more vsebovati gumba.');
  return problems;
}

// ---------------------------------------------------------------------------
// Shipped defaults — what "Povrni privzeto" restores
// ---------------------------------------------------------------------------

const ph = (key: PlaceholderKey) => `<span data-ph="${key}">{{${key}}}</span>`;
const block = (key: PlaceholderKey) => `<div data-ph="${key}">{{${key}}}</div>`;

export interface MailTemplateBody {
  subject: string;
  body: string;
}

/*
 * Written to read the same to one person and to a couple sharing an inbox:
 * the greeting takes ⟦ime⟧ as "Ana" or "Ana in Miha", the app puts each
 * person's name over their part of a block and on their own button, and the
 * prose itself never conjugates for a reader — Slovenian would need singular,
 * dual and plural forms of every "tvoj" and "odpri", and a household is not
 * worth a second template that drifts from the first.
 */
export const MAIL_TEMPLATE_DEFAULTS: Record<MailTemplateKey, Record<Lang, MailTemplateBody>> = {
  uvodno: {
    sl: {
      subject: `Redarstvo na {{dogodek}} — hvala za prijavo!`,
      body: [
        `<p>Živjo, ${ph('ime')}!</p>`,
        `<p>Hvala za prijavo med redarje na ${ph('dogodek')}. Dogodek je ${ph('datumi_dogodka')}, mi pa zdaj sestavljamo razpored.</p>`,
        `<h3>Kaj sledi</h3>`,
        `<ul><li>Izmena na redarskem mestu ob trasi, na parkirišču ali v štartno-ciljni areni.</li><li>Pred dogodkom pošljemo napotnico z natančnim razporedom, zbornim mestom in navodili.</li><li>Za hrano, pijačo in majico je poskrbljeno.</li></ul>`,
        `<p>Trenutno predvidevamo:</p>`,
        block('razpored'),
        `<p>Če kaj ne ustreza ali se pojavi vprašanje, je dovolj odgovor na to sporočilo — pride neposredno k nam.</p>`,
        `<p>Lep pozdrav,<br>${ph('koordinator')}</p>`,
      ].join(''),
    },
    en: {
      subject: `Marshalling at {{dogodek}} — thanks for signing up!`,
      body: [
        `<p>Hi ${ph('ime')},</p>`,
        `<p>thanks for signing up as a marshal at ${ph('dogodek')}. The event runs ${ph('datumi_dogodka')} and we are putting the roster together now.</p>`,
        `<h3>What to expect</h3>`,
        `<ul><li>A shift at a marshal post on the course, in a car park or in the start/finish arena.</li><li>Before the event we send a briefing with the exact schedule, meeting point and instructions.</li><li>Food, drink and a shirt are provided.</li></ul>`,
        `<p>What we have pencilled in so far:</p>`,
        block('razpored'),
        `<p>If something does not work, or there is a question, just reply to this email — it reaches us directly.</p>`,
        `<p>Best,<br>${ph('koordinator')}</p>`,
      ].join(''),
    },
  },
  napotnica: {
    sl: {
      subject: `Razpored — {{dogodek}}`,
      body: [
        `<p>Živjo, ${ph('ime')}!</p>`,
        `<p>Razpored za ${ph('dogodek')}:</p>`,
        block('razpored'),
        `<p>Celoten brifing — navodila, zborna mesta, zemljevid in gumb za potrditev — je na osebni povezavi spodaj:</p>`,
        `<a data-cta href="{{povezava}}">Odpri brifing</a>`,
        `<p>Povezava je osebna. Prosimo za potrditev udeležbe prek nje; vprašanja so dobrodošla kar z odgovorom na to sporočilo.</p>`,
        `<p>Lep pozdrav,<br>${ph('koordinator')}</p>`,
      ].join(''),
    },
    en: {
      subject: `Marshal schedule — {{dogodek}}`,
      body: [
        `<p>Hi ${ph('ime')},</p>`,
        `<p>here is the marshal schedule for ${ph('dogodek')}:</p>`,
        block('razpored'),
        `<p>The full briefing — instructions, meeting points, map and the confirmation button — is on the personal link below:</p>`,
        `<a data-cta href="{{povezava}}">Open the briefing</a>`,
        `<p>The link is personal. Please confirm attendance through it; questions are welcome as a reply to this email.</p>`,
        `<p>Best,<br>${ph('koordinator')}</p>`,
      ].join(''),
    },
  },
  sprememba: {
    sl: {
      subject: `Sprememba razporeda — {{dogodek}}`,
      body: [
        `<p>Živjo, ${ph('ime')}!</p>`,
        `<p>Razpored se je od zadnje napotnice spremenil:</p>`,
        block('spremembe'),
        `<p>Tako je videti zdaj:</p>`,
        block('razpored'),
        `<a data-cta href="{{povezava}}">Poglej in potrdi</a>`,
        `<p>Če novi termin ne ustreza, je dovolj odgovor na to sporočilo.</p>`,
        `<p>Lep pozdrav,<br>${ph('koordinator')}</p>`,
      ].join(''),
    },
    en: {
      subject: `Schedule change — {{dogodek}}`,
      body: [
        `<p>Hi ${ph('ime')},</p>`,
        `<p>the schedule has changed since the last briefing we sent:</p>`,
        block('spremembe'),
        `<p>This is how it looks now:</p>`,
        block('razpored'),
        `<a data-cta href="{{povezava}}">Review and confirm</a>`,
        `<p>If the new time does not work, just reply to this email.</p>`,
        `<p>Best,<br>${ph('koordinator')}</p>`,
      ].join(''),
    },
  },
  zahvala: {
    sl: {
      subject: `Hvala za {{dogodek}}!`,
      body: [
        `<p>Živjo, ${ph('ime')}!</p>`,
        `<p>Hvala za pomoč na ${ph('dogodek')}. Brez redarjev na trasi tekme ni — vsaka izmena je bila del tega.</p>`,
        `<p>Vidimo se prihodnje leto.</p>`,
        `<p>Lep pozdrav,<br>${ph('koordinator')}</p>`,
      ].join(''),
    },
    en: {
      subject: `Thank you for {{dogodek}}!`,
      body: [
        `<p>Hi ${ph('ime')},</p>`,
        `<p>thank you for helping at ${ph('dogodek')}. There is no race without marshals on the course — every shift was part of that.</p>`,
        `<p>See you next year.</p>`,
        `<p>Best,<br>${ph('koordinator')}</p>`,
      ].join(''),
    },
  },
  dnevno: {
    sl: {
      subject: `Danes: vaša izmena na {{dogodek}}`,
      body: [
        `<p>Dobro jutro, ${ph('ime')}!</p>`,
        `<p>Danes vas pričakujemo na mestu:</p>`,
        block('razpored'),
        `<p>Ko pridete na mesto, na strani z navodili pritisnite <strong>Prijavite prihod</strong>, ob odhodu pa <strong>Odjavite odhod</strong>. Tako vemo, da je mesto pokrito.</p>`,
        `<a data-cta href="{{povezava}}">Odprite navodila in se prijavite</a>`,
        `<p>Če zamujate ali ne morete priti, pokličite operativni center: ${ph('telefon_operative')}. V nujnih primerih 112.</p>`,
        `<p>Hvala in lep dan na progi!<br>${ph('koordinator')}</p>`,
      ].join(''),
    },
    en: {
      subject: `Today: your shift at {{dogodek}}`,
      body: [
        `<p>Good morning, ${ph('ime')}!</p>`,
        `<p>We are expecting you today at:</p>`,
        block('razpored'),
        `<p>When you arrive, tap <strong>Check in</strong> on your briefing page, and <strong>Check out</strong> when you leave. That way we know the post is covered.</p>`,
        `<a data-cta href="{{povezava}}">Open the briefing and check in</a>`,
        `<p>Running late or unable to come? Call the operations centre: ${ph('telefon_operative')}. In an emergency, 112.</p>`,
        `<p>Thank you, and have a great day on the course!<br>${ph('koordinator')}</p>`,
      ].join(''),
    },
  },
};

/** First name for a greeting; the whole string when it is a single word. */
export function firstName(fullName: string): string {
  return fullName.trim().split(/\s+/)[0] ?? '';
}

/**
 * Names as a greeting lists them: "Ana in Miha", "Ana, Miha in Nejc". What
 * ⟦ime⟧ resolves to in a mail several people read.
 */
export function joinNames(names: readonly string[], lang: Lang): string {
  const and = lang === 'en' ? 'and' : 'in';
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} ${and} ${names.at(-1)}`;
}

/**
 * A briefing link's token, wherever one appears in text.
 *
 * The token is the credential — whoever holds the link is the volunteer — so
 * it lives in the mail that carried it and nowhere else: not in the thread's
 * copy, not in a prompt. See {@link redactMagicLinks}.
 */
export const MAGIC_LINK_RE = /\/b\/[A-Za-z0-9_-]{16,}/g;

/**
 * The text with every briefing token blanked to `/b/…`. Where a link pointed
 * stays readable; what it would open does not. A coordinator who needs the
 * link mints a fresh one from the person's card, never copies it out of
 * history.
 */
export function redactMagicLinks(text: string): string {
  return text.replaceAll(MAGIC_LINK_RE, '/b/…');
}
